import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import dayjs from "dayjs";
import { useGetDSMMetersQuery, useUpdateDeviceForcedStatusMutation, useGetActiveSessionQuery } from "./operatorDashboardAPISlice";
import { readingOrDash } from "./displayValues";

// Design token reference (from switching.html :root)
// --surface-2: #1C232C  --border: rgba(255,255,255,0.06)  --border-strong: rgba(255,255,255,0.12)
// --accent: #FDB940  --accent-soft: rgba(253,185,64,0.12)  --accent-tint: rgba(253,185,64,0.06)
// --text-secondary: rgba(255,255,255,0.65)  --text-muted: rgba(255,255,255,0.45)  --text-faint: rgba(255,255,255,0.35)
// --green-soft: rgba(34,197,94,0.12)  --green-strong: rgba(34,197,94,0.22)
// --red-soft: rgba(239,68,68,0.12)    --red-strong: rgba(239,68,68,0.32)
// --blue-soft: rgba(59,130,246,0.14)  --purple-soft: rgba(165,180,252,0.16)  --purple: #A5B4FC

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#1C232C", borderRadius: "8px", fontSize: "0.88rem", color: "#fff",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(253,185,64,0.45) !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "10px 12px" },
  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.35)" },
};

// ── Amber letter badge (A, B, C…) ────────────────────────────────────────────
function Badge({ letter }) {
  return (
    <Box sx={{
      width: 18, height: 18, borderRadius: "4px", flexShrink: 0,
      background: "rgba(253,185,64,0.08)", border: "1px solid rgba(253,185,64,0.22)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#FDB940", lineHeight: 1, fontFamily: "monospace" }}>
        {letter}
      </Typography>
    </Box>
  );
}

// ── Section label row (badge + uppercase label + optional info icon) ──────────
// info may be a plain string or { title, body } for a two-part tooltip
function FieldLabel({ letter, children, info }) {
  const tooltipContent = info
    ? typeof info === "object"
      ? (
        <Box>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#fff", mb: "4px" }}>{info.title}</Typography>
          <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.5, color: "rgba(255,255,255,0.65)" }}>{info.body}</Typography>
        </Box>
      )
      : <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.5, color: "rgba(255,255,255,0.75)" }}>{info}</Typography>
    : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
      <Badge letter={letter} />
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.08em", color: "rgba(255,255,255,0.45)", textTransform: "uppercase" }}>
        {children}
      </Typography>
      {tooltipContent && (
        <Tooltip
          title={tooltipContent}
          placement="top" arrow
          componentsProps={{
            tooltip: { sx: { bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", p: "10px 12px", maxWidth: 300, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" } },
            arrow:   { sx: { color: "#1C232C" } },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "13px", color: "rgba(255,255,255,0.28)", cursor: "help", "&:hover": { color: "#FDB940" } }} />
        </Tooltip>
      )}
    </Box>
  );
}

// ── Helper text beneath a field ───────────────────────────────────────────────
function Helper({ children }) {
  return (
    <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", mt: "6px", lineHeight: 1.5 }}>
      {children}
    </Typography>
  );
}

// ── Mode toggle: All | Select ─────────────────────────────────────────────────
function ModeToggle({ value, onChange }) {
  const btn = (label, val) => {
    const active = value === val;
    return (
      <ButtonBase
        onClick={() => onChange(val)}
        sx={{
          borderRadius: "6px", px: "18px", py: "8px",
          fontSize: "0.82rem", fontWeight: 600, lineHeight: 1, transition: "all 0.18s",
          bgcolor: active ? "rgba(253,185,64,0.12)" : "transparent",
          color:   active ? "#FDB940" : "rgba(255,255,255,0.45)",
          boxShadow: active ? "inset 0 0 0 1px rgba(253,185,64,0.22)" : "none",
        }}
      >
        {label}
      </ButtonBase>
    );
  };
  return (
    <Box sx={{ display: "inline-flex", bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "9px", p: "3px", gap: "2px" }}>
      {btn("All", "all")}
      {btn("Select", "select")}
    </Box>
  );
}

// ── Switch status toggle: Force On | Force Off ────────────────────────────────
function StatusToggle({ value, onChange }) {
  const btn = (label, val, activeTextColor, activeBg, activeBorder) => {
    const active = value === val;
    return (
      <ButtonBase
        onClick={() => onChange(val)}
        sx={{
          borderRadius: "6px", px: "18px", py: "8px",
          display: "flex", alignItems: "center", gap: "6px",
          fontSize: "0.82rem", fontWeight: 600, lineHeight: 1, transition: "all 0.18s",
          bgcolor: active ? activeBg : "transparent",
          color:   active ? activeTextColor : "rgba(255,255,255,0.45)",
          boxShadow: active ? `inset 0 0 0 1px ${activeBorder}` : "none",
        }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor", opacity: active ? 1 : 0.45, flexShrink: 0 }} />
        {label}
      </ButtonBase>
    );
  };
  return (
    <Box sx={{ display: "inline-flex", bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "9px", p: "3px", gap: "2px" }}>
      {btn("Force On",  "force-on",  "#86EFAC", "rgba(34,197,94,0.12)",  "rgba(34,197,94,0.22)")}
      {btn("Force Off", "force-off", "#FCA5A5", "rgba(239,68,68,0.12)",  "rgba(239,68,68,0.32)")}
    </Box>
  );
}

// ── Number input with right-side unit label ───────────────────────────────────
function UnitInput({ value, onChange }) {
  return (
    <TextField
      type="number"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      inputProps={{ min: 0 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", pl: "8px", borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
              min after activation
            </Typography>
          </InputAdornment>
        ),
      }}
      sx={{
        ...INPUT_SX,
        "& .MuiInputBase-input": { padding: "10px 12px" },
        "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": { WebkitAppearance: "none" },
        "& input[type=number]": { MozAppearance: "textfield" },
      }}
    />
  );
}

// Rows per page in the per-device table. The table used to fetch 1000 devices in one
// request and render them all; there are 3930, so it silently showed a quarter of the
// fleet and cost about 9.5 seconds a load.
const DEVICE_PAGE_SIZE = 50;

// How long after a MaxReturn override the banner still explains it. Long enough that
// an operator returning to the page sees why their switch ended, short enough that a
// stale session is not presented as news.
const CANCELLED_BANNER_WINDOW_MS = 30 * 60 * 1000;

// ── Control state config (from switching.html CSS) ────────────────────────────
const CONTROL_CONFIG = {
  "nil":        { label: "Nil",        bg: "rgba(255,255,255,0.06)",   color: "rgba(255,255,255,0.35)", shadow: "none" },
  "ps":         { label: "PS",         bg: "rgba(59,130,246,0.14)",    color: "#93C5FD",               shadow: "none" },
  "ls":         { label: "LS",         bg: "rgba(253,185,64,0.12)",    color: "#FDB940",               shadow: "none" },
  "waiting":    { label: "Waiting",    bg: "rgba(253,185,64,0.06)",    color: "#FDE68A",               shadow: "none" },
  "recovering": { label: "Recovering", bg: "rgba(165,180,252,0.16)",   color: "#A5B4FC",               shadow: "none" },
  "forced-on":  { label: "Forced On",  bg: "rgba(34,197,94,0.12)",     color: "#86EFAC",               shadow: "inset 0 0 0 1px rgba(34,197,94,0.22)" },
  "forced-off": { label: "Forced Off", bg: "rgba(239,68,68,0.12)",     color: "#FCA5A5",               shadow: "inset 0 0 0 1px rgba(239,68,68,0.32)" },
};


// ── Switch status chip ───────────────────────────────────────────────────────
// Was a connection chip reading d.connection_status, which DMSMeterSerializer has never
// served - its field list goes straight from switch_status to serial_number. The
// comparison was therefore always undefined === "Connected", so every row on the page
// read "Disconnected" whatever the device was doing.
//
// It was redundant as well as broken: DMSMeterView defaults to Q(connected=True), and
// this page never sends connectionStatus, so in the default view every row IS
// connected. A search used to skip that default (views.py, "elif not searchquery"); the
// page now sends connectionStatus explicitly, so search results are connected devices too
// (NEU-1139). Do not restore this chip, which showed nothing.
//
// Showing the relay state is what the operator is here for (Dale, 2026-10-22).
function SwitchChip({ switchStatus }) {
  const on = switchStatus === "On";
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      fontSize: "0.7rem", fontWeight: 600, px: "9px", py: "3px",
      borderRadius: "999px", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
      fontFamily: "inherit",
      bgcolor: on ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.06)",
      color:   on ? "#86EFAC" : "rgba(255,255,255,0.35)",
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
      {switchStatus ?? "Unknown"}
    </Box>
  );
}

// ── Control state chip ────────────────────────────────────────────────────────
function ControlChip({ control }) {
  const { label, bg, color, shadow } = CONTROL_CONFIG[control] ?? CONTROL_CONFIG["nil"];
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      fontSize: "0.7rem", fontWeight: 600, px: "10px", py: "4px",
      borderRadius: "999px", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
      fontFamily: "inherit",
      bgcolor: bg, color, boxShadow: shadow,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", opacity: 0.85 }} />
      {label}
    </Box>
  );
}

// ── Ghost bordered row action button ─────────────────────────────────────────
function RowBtn({ label, variant = "default", onClick }) {
  const styles = {
    on:      { border: "1px solid rgba(34,197,94,0.35)",  color: "#86EFAC",               hoverBg: "rgba(34,197,94,0.12)",  hoverBorder: "rgba(34,197,94,0.22)",  hoverColor: "#86EFAC" },
    off:     { border: "1px solid rgba(239,68,68,0.35)",  color: "#FCA5A5",               hoverBg: "rgba(239,68,68,0.12)", hoverBorder: "rgba(239,68,68,0.32)", hoverColor: "#FCA5A5" },
    reset:   { border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.45)", hoverBg: "rgba(255,255,255,0.03)", hoverBorder: "rgba(255,255,255,0.22)", hoverColor: "#fff" },
    default: { border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.65)", hoverBg: "rgba(255,255,255,0.03)", hoverBorder: "rgba(255,255,255,0.22)", hoverColor: "#fff" },
  };
  const s = styles[variant] ?? styles.default;
  return (
    <ButtonBase onClick={onClick} sx={{
      borderRadius: "7px", px: "11px", py: "6px",
      fontSize: "0.74rem", fontWeight: 600, lineHeight: 1,
      background: "transparent", border: s.border, color: s.color, transition: "all 0.15s",
      "&:hover": { bgcolor: s.hoverBg, borderColor: s.hoverBorder, color: s.hoverColor },
    }}>
      {label}
    </ButtonBase>
  );
}

// ── Confirm modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ open, onClose, onConfirm, direction, scope, start, alarm, expiry }) {
  if (!open) return null;
  const isOn = direction === "force-on";
  return (
    <Box
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      sx={{
        position: "fixed", inset: 0, zIndex: 1300,
        bgcolor: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        p: "24px",
      }}
    >
      <Box sx={{
        bgcolor: "#0D1117", border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: "14px", p: "24px", width: "100%", maxWidth: 480,
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#fff", mb: "8px" }}>
          Confirm force switch
        </Typography>
        <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
          Review the parameters below before activating. This override will take effect
          for the entire fleet and cannot be silently undone.
        </Typography>

        <Box sx={{
          display: "grid", gridTemplateColumns: "auto 1fr",
          gap: "6px 14px", my: "16px", fontSize: "0.85rem",
        }}>
          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 500, whiteSpace: "nowrap", fontSize: "inherit" }}>Direction</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "inherit" }}>
            {isOn ? "Force On — element energised" : "Force Off — element disabled"}
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 500, whiteSpace: "nowrap", fontSize: "inherit" }}>Scope</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "inherit" }}>{scope}</Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 500, whiteSpace: "nowrap", fontSize: "inherit" }}>Start</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "inherit" }}>{start}</Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 500, whiteSpace: "nowrap", fontSize: "inherit" }}>Alarm</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "inherit" }}>{alarm}</Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 500, whiteSpace: "nowrap", fontSize: "inherit" }}>Expiry</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "inherit" }}>{expiry}</Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-end", mt: "22px" }}>
          <ButtonBase
            onClick={onClose}
            sx={{
              bgcolor: "transparent", border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.65)", px: "14px", py: "8px",
              borderRadius: "8px", fontSize: "0.85rem",
              "&:hover": { bgcolor: "rgba(255,255,255,0.04)", color: "#fff" },
            }}
          >
            Cancel
          </ButtonBase>
          <ButtonBase
            onClick={onConfirm}
            sx={{
              border: "none", px: "20px", py: "8px", borderRadius: "8px",
              fontSize: "0.85rem", fontWeight: 700,
              bgcolor: isOn ? "#22C55E" : "#EF4444",
              color:   isOn ? "#052E16" : "#FEE2E2",
              "&:hover": { bgcolor: isOn ? "#1FB155" : "#DC2626" },
            }}
          >
            {isOn ? "Confirm Force On" : "Confirm Force Off"}
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}

// ── Single device row ──────────────────────────────────────────────────────────
function DeviceRow({ device, checked, onToggle, onForce, dimmed, pending }) {
  return (
    <Box component="tr" sx={{
      borderBottom: "1px solid rgba(255,255,255,0.06)", transition: "background 0.12s, opacity 0.2s, filter 0.2s",
      "&:hover": { bgcolor: dimmed ? "transparent" : "rgba(253,185,64,0.06)" },
      opacity: dimmed ? 0.15 : 1,
      filter: dimmed ? "saturate(0.2)" : "none",
    }}>
      <Box component="td" sx={{ pl: "22px", pr: 0, py: "14px", verticalAlign: "middle" }}>
        <input type="checkbox" checked={checked} onChange={onToggle}
          style={{ accentColor: "#FDB940", cursor: "pointer", width: 17, height: 17, display: "block" }} />
      </Box>
      <Box component="td" sx={{ px: "16px", py: "14px", verticalAlign: "middle" }}>
        <Box sx={{
          display: "inline-flex", alignItems: "center",
          fontFamily: "monospace", fontSize: "0.78rem",
          px: "11px", py: "5px", borderRadius: "999px",
          bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)",
          color: "#fff", transition: "all 0.15s",
          "tr:hover &": { borderColor: "rgba(253,185,64,0.4)", color: "#FDB940", bgcolor: "rgba(253,185,64,0.06)" },
        }}>
          {device.id}
        </Box>
      </Box>
      <Box component="td" sx={{ px: "16px", py: "14px", verticalAlign: "middle" }}>
        <SwitchChip switchStatus={device.switchStatus} />
      </Box>
      {/* shows forced status when set, otherwise shows control_status */}
      <Box component="td" sx={{ px: "16px", py: "14px", verticalAlign: "middle" }}>
        <ControlChip control={
          device.forcedStatus === "ForcedOn"  ? "forced-on" :
          device.forcedStatus === "ForcedOff" ? "forced-off" :
          device.control
        } />
      </Box>
      <Box component="td" sx={{ px: "16px", py: "14px", textAlign: "right", verticalAlign: "middle" }}>
        <Typography sx={{ fontSize: "0.85rem", color: "#fff", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{device.hwi}</Typography>
      </Box>
      <Box component="td" sx={{ px: "16px", py: "14px", textAlign: "right", verticalAlign: "middle" }}>
        <Typography sx={{ fontSize: "0.85rem", color: "#fff", fontWeight: 500, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {device.power}
        </Typography>
      </Box>
      <Box component="td" sx={{ px: "16px", py: "14px", textAlign: "right", verticalAlign: "middle" }}>
        <Typography sx={{ fontSize: "0.85rem", fontVariantNumeric: "tabular-nums", color: device.timeSince != null ? "#fff" : "rgba(255,255,255,0.35)", fontWeight: device.timeSince != null ? 500 : 400 }}>
          {device.timeSince != null ? device.timeSince : "—"}
        </Typography>
      </Box>
      <Box component="td" sx={{ px: "16px", pr: "22px", py: "14px", textAlign: "right", verticalAlign: "middle", whiteSpace: "nowrap" }}>
        {pending ? (
          <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "#FDB940", fontStyle: "italic" }}>
            Updating…
          </Typography>
        ) : (
          <Box sx={{ display: "inline-flex", gap: "6px" }}>
            {device.forcedStatus === "Nil" && (
              <>
                <RowBtn label="Force On"  variant="on"  onClick={() => onForce("ForcedOn")} />
                <RowBtn label="Force Off" variant="off" onClick={() => onForce("ForcedOff")} />
              </>
            )}
            {device.forcedStatus === "ForcedOn" && (
              <>
                <RowBtn label="Force Off" variant="off"   onClick={() => onForce("ForcedOff")} />
                <RowBtn label="Reset"     variant="reset"  onClick={() => onForce("Nil")} />
              </>
            )}
            {device.forcedStatus === "ForcedOff" && (
              <>
                <RowBtn label="Force On"  variant="on"    onClick={() => onForce("ForcedOn")} />
                <RowBtn label="Reset"     variant="reset"  onClick={() => onForce("Nil")} />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Per-device override table ──────────────────────────────────────────────────
function PerDeviceTable({ mode, onRequestSelectionForce, selectionBusy }) {
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage]         = useState(0);

  // Debounced so typing a serial does not fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to what is being looked at puts you back on the first page; staying on
  // page 7 of a result set that now has two pages shows an empty table.
  useEffect(() => { setPage(0); }, [debouncedSearch, filter]);

  // Search and filtering are done by the database, not in the browser. They used to run
  // over a single 1000-row fetch, which capped the table at 1000 of 3930 devices and
  // meant a search could only ever find one of those 1000.
  const queryArgs = useMemo(() => {
    // Connected devices, asked for explicitly: the backend's connected default is dropped
    // when a search is sent, so typing a search changed the population and the counts with
    // it (NEU-1139). The page is about connected Tjommies throughout.
    const args = { page, pagesize: DEVICE_PAGE_SIZE, connectionStatus: "Connected" };
    if (debouncedSearch) args.searchQuery = debouncedSearch;
    // "Forced" is either direction, which is why the backend takes a list here.
    if (filter === "forced")   args.forcedStatus = "ForcedOn,ForcedOff";
    if (filter === "unforced") args.forcedStatus = "Nil";
    return args;
  }, [page, debouncedSearch, filter]);

  // Every device, baselines included. Forced switching and MaxReturn both operate on
  // the full list, so a baseline can be forced and must be resettable from here.
  //
  // This used to pass isBaseline: "False", which did the opposite of what it reads
  // like: the backend tests the raw query string for truthiness, and "False" is a
  // non-empty string, so the table showed only the baselines.
  const { data: metersData, isLoading: isMetersLoading, isFetching: isMetersFetching } =
    useGetDSMMetersQuery(queryArgs, { pollingInterval: 60000 });

  // The forced total is a property of the whole fleet, not of this page, so it needs its
  // own count. pagesize 1 because only total_devices is read.
  const { data: forcedCountData } = useGetDSMMetersQuery(
    { page: 0, pagesize: 1, forcedStatus: "ForcedOn,ForcedOff", connectionStatus: "Connected" },
    { pollingInterval: 60000 }
  );
  const forcedCount = forcedCountData?.total_devices ?? 0;

  const totalDevices = metersData?.total_devices ?? 0;
  const pageCount    = Math.max(1, Math.ceil(totalDevices / DEVICE_PAGE_SIZE));

  const [updateForcedStatus] = useUpdateDeviceForcedStatusMutation();
  const [pendingDevices, setPendingDevices] = useState(new Set());

  // Clear pending state when fresh meter data arrives (refetch after mutation)
  useEffect(() => {
    if (pendingDevices.size > 0) setPendingDevices(new Set());
  }, [metersData]); // eslint-disable-line react-hooks/exhaustive-deps

  // The page clears the selection by bumping this once a selection force has been
  // applied. Kept here rather than in the page so the selection stays owned by the
  // table that renders the checkboxes.
  useEffect(() => {
    if (selectionBusy === "done") setSelected(new Set());
  }, [selectionBusy]);

  const devices = useMemo(() => {
    if (!metersData?.data) return [];
    return metersData.data.map(d => {
      const rawCtrl = (d.control_status ?? "Nil").toLowerCase();
      // Map API control statuses to CONTROL_CONFIG keys
      // control_status spellings differ from the CONTROL_CONFIG style keys, and a miss
      // here renders the device as "Nil" rather than failing visibly - which is how
      // forced devices came to show no forced chip at all.
      const ctrlKey =
        rawCtrl === "maxnil"    ? "nil" :
        rawCtrl === "maxrec"    ? "recovering" :
        rawCtrl === "forcedon"  ? "forced-on" :
        rawCtrl === "forcedoff" ? "forced-off" :
        CONTROL_CONFIG[rawCtrl] ? rawCtrl :
        "nil";
      let timeSince = null;
      const isActivelyControlled = (d.forced_status ?? "Nil") !== "Nil" || ctrlKey !== "nil";
      if (isActivelyControlled && d.last_switch_controlled) {
        const diffMin = Math.round((Date.now() - new Date(d.last_switch_controlled).getTime()) / 60000);
        if (diffMin >= 0) timeSince = diffMin;
      }
      return {
        id:           d.serial_number,
        switchStatus: d.switch_status ?? null,
        control:      ctrlKey,
        forcedStatus: d.forced_status ?? "Nil",
        // "—" for no HWI, a stale one (older than 2 h, null from the API) or a device with no
        // geyser; it showed 0 or NaN (NEU-1133). Power arrives in kW (NEU-1126).
        hwi:          readingOrDash(d.hwi),
        power:        readingOrDash(d.power),
        timeSince,
      };
    });
  }, [metersData]);

  // A row button is a bare toggle: it takes effect immediately and opens no session,
  // which is what scope "Device" tells the backend.
  const handleForce = (serialNumber, forcedStatus) => {
    setPendingDevices(prev => new Set(prev).add(serialNumber));
    updateForcedStatus({
      forced_status: forcedStatus,
      serial_numbers: [serialNumber],
      scope: "Device",
    });
  };

  // The selection bar is not a bigger row button - it is a switching session over a
  // chosen set, carrying the same alarm, expiry and banners as a fleet-wide override.
  // The page owns all of that, so the table only raises the intent and the page
  // confirms it and fires it. See handleSelectionForceConfirm.
  const handleBulkForce = (forcedStatus) => {
    onRequestSelectionForce(forcedStatus, [...selected]);
  };

  // The server has already applied the search and the filter, so this page is what is
  // shown. Kept as `visible` so the selection logic below reads the same as before.
  const visible = devices;

  const allChecked    = visible.length > 0 && visible.every(d => selected.has(d.id));
  const indeterminate = !allChecked && visible.some(d => selected.has(d.id));
  const selectedCount = selected.size;

  const toggleAll = () => {
    setSelected(prev => {
      const s = new Set(prev);
      if (allChecked) visible.forEach(d => s.delete(d.id));
      else            visible.forEach(d => s.add(d.id));
      return s;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const dimmed = mode === "all";

  return (
    <Box sx={{ mt: "20px", bgcolor: "#161B22", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", overflow: "hidden" }}>

      {/* ── Card header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", px: "22px", py: "16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", px: "8px", py: "3px", borderRadius: "4px", bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1, fontFamily: "monospace" }}>
              Per-device
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff" }}>Per-device override</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>
              Inherits <strong style={{ color: "rgba(255,255,255,0.65)" }}>Switch Status</strong>,{" "}
              <strong style={{ color: "rgba(255,255,255,0.65)" }}>Start time</strong>,{" "}
              <strong style={{ color: "rgba(255,255,255,0.65)" }}>Alarm</strong> and{" "}
              <strong style={{ color: "rgba(255,255,255,0.65)" }}>Expiry</strong> from the bulk settings above.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Selection bar (visible when rows are checked in select mode) ── */}
      {!dimmed && selectedCount > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", px: "22px", py: "10px", bgcolor: "rgba(253,185,64,0.06)", borderBottom: "1px solid rgba(253,185,64,0.18)", fontSize: "0.84rem", color: "rgba(255,255,255,0.65)" }}>
          <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: "#FDB940" }}>{selectedCount}</Typography>
          <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.65)" }}>{selectedCount === 1 ? "device selected" : "devices selected"}</Typography>
          {pendingDevices.size > 0 ? (
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#FDB940", fontStyle: "italic" }}>
              Updating…
            </Typography>
          ) : (
            <Box sx={{ display: "inline-flex", gap: "8px" }}>
              <RowBtn label="Force On"  variant="on"    onClick={() => handleBulkForce("ForcedOn")} />
              <RowBtn label="Force Off" variant="off"   onClick={() => handleBulkForce("ForcedOff")} />
              <RowBtn label="Reset"     variant="reset"  onClick={() => handleBulkForce("Nil")} />
            </Box>
          )}
          <ButtonBase onClick={clearSelection} sx={{ ml: "auto", fontSize: "0.76rem", color: "rgba(255,255,255,0.45)", px: "8px", py: "4px", borderRadius: "6px", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.04)" } }}>
            Clear selection
          </ButtonBase>
        </Box>
      )}

      {/* ── Toolbar ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px", px: "22px", py: "14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", px: "12px", py: "8px", minWidth: 220 }}>
          <SearchRoundedIcon sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
          <Box
            component="input"
            type="search"
            placeholder="Search serial number — e.g. TJ-234563"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ "&::placeholder": { fontFamily: "inherit" } }}
            style={{ background: "transparent", border: "none", outline: "none", color: "#fff", font: "inherit", fontSize: "0.82rem", flex: 1, minWidth: 0, fontFamily: "inherit" }}
          />
        </Box>
        {/* Filter pills */}
        <Box sx={{ display: "inline-flex", bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", p: "2px" }}>
          {["all", "forced", "unforced"].map(f => (
            <ButtonBase key={f} onClick={() => setFilter(f)} sx={{
              borderRadius: "6px", px: "12px", py: "5px",
              fontSize: "0.76rem", fontWeight: 600, lineHeight: 1, transition: "all 0.15s",
              bgcolor: filter === f ? "rgba(253,185,64,0.12)" : "transparent",
              color:   filter === f ? "#FDB940" : "rgba(255,255,255,0.45)",
              textTransform: "capitalize",
            }}>
              {f}
            </ButtonBase>
          ))}
        </Box>
        <Typography sx={{ ml: "auto", fontSize: "0.76rem", color: "rgba(255,255,255,0.45)", fontVariantNumeric: "tabular-nums" }}>
          <strong style={{ color: "#fff" }}>{totalDevices.toLocaleString()}</strong> devices
          {totalDevices > DEVICE_PAGE_SIZE && (
            <> · showing <strong style={{ color: "#fff" }}>{visible.length}</strong></>
          )}
          {" · "}<strong style={{ color: "#fff" }}>{forcedCount.toLocaleString()}</strong> forced
        </Typography>
      </Box>

      {/* ── Table + overlay wrapper ── */}
      <Box sx={{ position: "relative" }}>

        {/* Table — dimming is per-row so banner rows stay visible */}
        <Box sx={{
          pointerEvents: dimmed ? "none" : "auto",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: 640, fontFamily: "inherit" }}>
            <Box component="thead" sx={{
              opacity: dimmed ? 0.15 : 1,
              filter: dimmed ? "saturate(0.2)" : "none",
              transition: "opacity 0.2s, filter 0.2s",
            }}>
              <Box component="tr" sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", bgcolor: "rgba(255,255,255,0.015)" }}>
                <Box component="th" sx={{ width: 44, pl: "22px", pr: 0, py: "13px", textAlign: "left" }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={el => { if (el) el.indeterminate = indeterminate; }}
                    onChange={toggleAll}
                    style={{ accentColor: "#FDB940", cursor: "pointer", width: 17, height: 17, display: "block" }}
                  />
                </Box>
                {[
                  { label: "ID",                    unit: null,  align: "left",  pr: "16px" },
                  { label: "Switch",                unit: null,  align: "left",  pr: "16px" },
                  { label: "Control",               unit: null,  align: "left",  pr: "16px" },
                  { label: "HWI",                   unit: "(%)", align: "right", pr: "16px" },
                  { label: "Power",                 unit: "(kW)", align: "right", pr: "16px" },
                  { label: "Time Since Switch",     unit: "(m)", align: "right", pr: "16px" },
                  { label: "Action",                unit: null,  align: "right", pr: "22px" },
                ].map(col => (
                  <Box component="th" key={col.label} sx={{
                    px: "16px", pr: col.pr, py: "13px", textAlign: col.align,
                    fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.06em",
                    color: "rgba(255,255,255,0.45)", textTransform: "uppercase",
                    fontFamily: "inherit", whiteSpace: "nowrap",
                  }}>
                    {col.label}
                    {col.unit && (
                      <Box component="span" sx={{ ml: "3px", fontWeight: 400, opacity: 0.6, textTransform: "none", letterSpacing: 0, fontSize: "0.6rem" }}>
                        {col.unit}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody" sx={{ fontFamily: "inherit" }}>
              {isMetersLoading ? (
                <Box component="tr">
                  <Box component="td" colSpan={8} sx={{ px: "22px", py: "28px", textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: "0.82rem", fontFamily: "inherit" }}>
                    Loading devices…
                  </Box>
                </Box>
              ) : visible.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={8} sx={{ px: "22px", py: "28px", textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: "0.82rem", fontFamily: "inherit" }}>
                    No devices found
                  </Box>
                </Box>
              ) : visible.map((device) => (
                <React.Fragment key={device.id}>
                  <DeviceRow
                    device={device}
                    dimmed={dimmed}
                    pending={pendingDevices.has(device.id)}
                    checked={selected.has(device.id)}
                    onToggle={() => setSelected(prev => {
                      const s = new Set(prev);
                      s.has(device.id) ? s.delete(device.id) : s.add(device.id);
                      return s;
                    })}
                    onForce={(status) => handleForce(device.id, status)}
                  />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Overlay pill — sticky near the top so it's visible even on long tables */}
        {dimmed && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-start", justifyContent: "center", pointerEvents: "none", pt: "18px" }}>
            <Box sx={{
              position: "sticky", top: 80,
              display: "inline-flex", alignItems: "center", gap: "8px",
              bgcolor: "#1C232C", border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "999px", px: "18px", py: "10px",
              boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
            }}>
              <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.65)", fontWeight: 500 }}>
                Switch <strong style={{ color: "#FDB940", fontWeight: 700, margin: "0 3px" }}>Mode</strong>
                to <strong style={{ color: "#FDB940", fontWeight: 700, margin: "0 3px" }}>Select</strong>
                to override individual devices
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Pager ── */}
      {totalDevices > DEVICE_PAGE_SIZE && (
        <Box sx={{
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px",
          px: "22px", py: "12px", borderTop: "1px solid rgba(255,255,255,0.06)",
        }}>
          <Typography sx={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.45)", fontVariantNumeric: "tabular-nums" }}>
            {(page * DEVICE_PAGE_SIZE + 1).toLocaleString()}–
            {Math.min((page + 1) * DEVICE_PAGE_SIZE, totalDevices).toLocaleString()} of{" "}
            {totalDevices.toLocaleString()}
          </Typography>
          {isMetersFetching && (
            <Typography sx={{ fontSize: "0.76rem", color: "#FDB940", fontStyle: "italic" }}>
              Loading…
            </Typography>
          )}
          <Box sx={{ ml: "auto", display: "inline-flex", alignItems: "center", gap: "8px" }}>
            {[
              { label: "Previous", to: page - 1, disabled: page === 0 },
              { label: "Next",     to: page + 1, disabled: page >= pageCount - 1 },
            ].map(({ label, to, disabled }) => (
              <ButtonBase
                key={label}
                disabled={disabled}
                onClick={() => setPage(to)}
                sx={{
                  bgcolor: "transparent", border: "1px solid rgba(255,255,255,0.12)",
                  color: disabled ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.65)",
                  px: "14px", py: "6px", borderRadius: "7px", fontSize: "0.78rem", fontWeight: 600,
                  "&:hover": disabled ? {} : { bgcolor: "rgba(255,255,255,0.04)", color: "#fff" },
                }}
              >
                {label}
              </ButtonBase>
            ))}
            <Typography sx={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.45)", fontVariantNumeric: "tabular-nums", minWidth: 90, textAlign: "right" }}>
              Page {(page + 1).toLocaleString()} of {pageCount.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      )}

    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SwitchingPage() {
  const [mode, setMode]                 = useState("all");
  const [switchStatus, setSwitchStatus] = useState("force-on");
  const [startDateTime, setStartDateTime] = useState(() => dayjs().format("YYYY-MM-DDTHH:mm"));
  const [alarm, setAlarm]               = useState(5);
  const [expiry, setExpiry]             = useState(120);

  // Active switch state + confirm modal
  const [active, setActive]       = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed]     = useState(null);   // seconds since activation
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [bannerState, setBannerState]       = useState(null); // null | "alarm" | "expired"
  const [alarmDismissed, setAlarmDismissed] = useState(false);
  // Kept out of bannerState on purpose: a MaxReturn override can coincide with the
  // alarm, and a single-valued banner state would hide one behind the other.
  const [cancelled, setCancelled] = useState(null);
  // A selection force the operator has asked for but not yet confirmed.
  const [pendingSelection, setPendingSelection] = useState(null);  // {forcedStatus, serials}
  const [selectionBusy, setSelectionBusy]       = useState(null);  // null | "busy" | "done"
  const [conflict, setConflict]                 = useState(null);  // 409 from the backend
  const dismissedSessionRef = useRef(null);
  const tickRef = useRef(null);

  const [updateForcedStatus] = useUpdateDeviceForcedStatusMutation();
  // MaxReturn can end this override without the operator touching anything, and the
  // only signal is this endpoint. reconcile_switching_sessions runs on a 1-minute
  // cron, so polling at 15s keeps the worst case under about 75 seconds.
  const { data: sessionData } = useGetActiveSessionQuery(undefined, { pollingInterval: 15000 });

  // The open session is the source of truth for what the card displays, not the
  // toggles. Syncing only when `!active` was not enough: a session opened from the
  // selection bar can have a different direction from the toggle at the top, and the
  // card would keep showing the toggle's direction - "Active - Force On" in green over
  // a Force Off session - until the page was refreshed.
  //
  // Keyed on the session id so the operator can still move the toggles while a session
  // runs without them being yanked back on the next poll.
  const syncedSessionRef = useRef(null);
  useEffect(() => {
    if (!sessionData) return;
    const s = sessionData.active_session;

    if (s) {
      if (syncedSessionRef.current !== s.id) {
        syncedSessionRef.current = s.id;
        setSwitchStatus(s.direction === "ForcedOn" ? "force-on" : "force-off");
        setMode(s.scope === "Select" ? "select" : "all");
        setAlarm(s.alarm_minutes);
        setExpiry(s.expiry_minutes);
        // Null while a scheduled session is still pending (NEU-857).
        setStartedAt(s.activated_at ? new Date(s.activated_at).getTime() : null);
      }
      if (!active) setActive(true);
    } else {
      syncedSessionRef.current = null;
      if (active) {
        setActive(false);
        setStartedAt(null);
      }
    }
  }, [sessionData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Devices left forced with no session to own them. A row-button force records no
  // session, so nothing expires it and - until this banner - nothing on the page
  // offered a way to clear it: the deactivate dialog only opens while a session is
  // active. Three devices on staging sat forced on for fifteen hours that way.
  const orphanedForced = (!active && sessionData?.orphaned_forced_count) || 0;

  // Detect a MaxReturn override. The operator's switch can be ended by the demand
  // safety interlock with nothing on screen to say so, which is the failure this
  // page exists to prevent. Two shapes: the session closed outright (ended_by =
  // MaxReturn), or it is still open with some devices already shed.
  useEffect(() => {
    if (!sessionData) return;
    const open = sessionData.active_session;
    const last = sessionData.last_session;

    let next = null;
    if (!open && last?.ended_by === "MaxReturn") {
      // Only for a recent override. Without this, opening the page days later
      // resurfaces a long-dead session as though it just happened.
      const endedAt = last.deactivated_at ? new Date(last.deactivated_at).getTime() : 0;
      if (Date.now() - endedAt < CANCELLED_BANNER_WINDOW_MS) {
        next = { id: last.id, released: last.reverted_count, total: last.device_count, closed: true };
      }
    } else if (open?.reverted_count > 0) {
      next = { id: open.id, released: open.reverted_count, total: open.device_count, closed: false };
    }

    if (next && dismissedSessionRef.current !== next.id) setCancelled(next);
  }, [sessionData]);

  const isForceOn    = switchStatus === "force-on";
  const isSelectMode = mode === "select";

  // Elapsed timer — runs every 1s while active
  useEffect(() => {
    if (active && startedAt) {
      const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      tick();
      tickRef.current = setInterval(tick, 1000);
      return () => clearInterval(tickRef.current);
    } else {
      setElapsed(null);
      if (tickRef.current) clearInterval(tickRef.current);
    }
  }, [active, startedAt]);

  // Evaluate alarm / expiry thresholds each tick
  const expiredRef = useRef(false);
  useEffect(() => {
    if (!active || elapsed == null) return;
    const mins = Math.floor(elapsed / 60);
    // Expiry — auto-revert (hard enforcement)
    if (expiry > 0 && mins >= expiry && !expiredRef.current) {
      expiredRef.current = true;
      updateForcedStatus({ forced_status: "Nil" }).then(() => {
        setActive(false);
        setStartedAt(null);
        setBannerState("expired");
      });
      return;
    }
    // Alarm — warning only
    if (alarm > 0 && mins >= alarm && !alarmDismissed && bannerState !== "expired" && !expiredRef.current) {
      setBannerState("alarm");
    }
  }, [elapsed, active, alarm, expiry, alarmDismissed, bannerState, updateForcedStatus]);

  // Close modals on Escape
  useEffect(() => {
    if (!confirmOpen && !deactivateOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") { setConfirmOpen(false); setDeactivateOpen(false); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [confirmOpen, deactivateOpen]);

  const formatElapsed = useCallback((secs) => {
    if (secs == null) return null;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, []);

  // Switch button click: open confirm (idle) or deactivate confirm (active)
  const handleSwitchClick = () => {
    if (active || orphanedForced > 0) {
      setDeactivateOpen(true);
    } else {
      setConfirmOpen(true);
    }
  };

  // Activate confirm — fire the switch
  // Shared by both ways of opening a session - the whole fleet and a chosen set. The
  // timers, banners and dismissals are session state, not fleet state, so they reset
  // identically either way.
  const beginSession = (forcedStatus) => {
    // The card reads its direction from switchStatus, so a session opened from the
    // selection bar has to set it - otherwise the pill shows the toggle's direction
    // rather than the one that was just applied.
    if (forcedStatus) {
      setSwitchStatus(forcedStatus === "ForcedOn" ? "force-on" : "force-off");
    }
    setActive(true);
    setStartedAt(Date.now());
    setBannerState(null);
    setAlarmDismissed(false);
    setCancelled(null);
    dismissedSessionRef.current = null;
    expiredRef.current = false;
  };

  // Only one session may be open at a time - the backend enforces it with a partial
  // unique index and answers 409 rather than forcing devices it cannot record.
  const handleSessionError = (err) => {
    if (err?.status === 409 || err?.originalStatus === 409) {
      setConflict(err?.data?.error || "A switching session is already open.");
      return true;
    }
    return false;
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    updateForcedStatus({
      forced_status: isForceOn ? "ForcedOn" : "ForcedOff",
      scope: mode === "select" ? "Select" : "All",
      alarm_minutes: alarm,
      expiry_minutes: expiry,
    }).unwrap()
      .then(() => beginSession(isForceOn ? "ForcedOn" : "ForcedOff"))
      .catch(err => { if (!handleSessionError(err)) throw err; });
  };

  // Raised by the per-device table's selection bar. The set is chosen there; the alarm,
  // the expiry and the session live here, which is why the page fires this rather than
  // the table.
  const onRequestSelectionForce = useCallback((forcedStatus, serials) => {
    if (serials.length > 0) setPendingSelection({ forcedStatus, serials });
  }, []);

  const handleSelectionForceConfirm = () => {
    const { forcedStatus, serials } = pendingSelection;
    setPendingSelection(null);
    setSelectionBusy("busy");
    updateForcedStatus({
      forced_status: forcedStatus,
      serial_numbers: serials,
      scope: "Select",
      alarm_minutes: alarm,
      expiry_minutes: expiry,
    }).unwrap()
      .then(() => { beginSession(forcedStatus); setSelectionBusy("done"); })
      .catch(err => {
        setSelectionBusy(null);
        if (!handleSessionError(err)) throw err;
      });
  };

  // Deactivate confirm — reset all forced devices to Nil
  // Waits for the revert: the card used to reset at once whether or not the request worked,
  // and nothing told the operator either way (NEU-1059).
  const [deactivateToast, setDeactivateToast] = useState(null);  // null | {level, text}
  const handleDeactivateConfirm = async () => {
    setDeactivateOpen(false);
    try {
      await updateForcedStatus({ forced_status: "Nil" }).unwrap();
    } catch (err) {
      setDeactivateToast({
        level: "error",
        text: err?.data?.error_message || err?.data?.error || "Couldn't deactivate the override. It is still active; try again.",
      });
      return;
    }
    setActive(false);
    setStartedAt(null);
    setBannerState(null);
    setAlarmDismissed(false);
    setDeactivateToast({ level: "info", text: "Override deactivated — all affected Tjommies reset to Nil." });
  };

  // Dynamic summary text
  const dt          = dayjs(startDateTime);
  const formattedTime = dt.isValid() ? dt.format("HH:mm [on] DD/MM/YYYY") : "—";
  const modeLabel   = isSelectMode ? "selected Tjommies" : "all connected Tjommies";
  const actionLabel = isForceOn ? "Force On" : "Force Off";
  const alarmLabel  = alarm === 0 ? "no alarm" : `${alarm}-minute alarm after activation`;
  const expiryLabel = expiry === 0
    ? "no expiry (manual deactivate only)"
    : `${expiry}-minute expiry after activation`;

  // Switch button appearance
  const switchColor  = isForceOn ? "#22C55E" : "#EF4444";
  const switchShadow = isForceOn ? "0 6px 20px rgba(34,197,94,0.28)" : "0 6px 20px rgba(239,68,68,0.28)";
  const switchHoverBg    = isForceOn ? "#1FB155" : "#DC2626";
  const switchHoverShadow = isForceOn ? "0 8px 26px rgba(34,197,94,0.36)" : "0 8px 26px rgba(239,68,68,0.36)";

  return (
    <Box sx={{ p: "32px 28px", maxWidth: 1100, mx: "auto" }}>

      {/* ── Page header ── */}
      <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.15 }}>
        Force-On / Force-Off
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.45)", mt: "4px", maxWidth: 640, lineHeight: 1.6 }}>
        Override the normal DSM schedule and switch all Tjommies — or a filtered selection — on or
        off. Scheduled start, an activation alarm warning and auto-expiry are supported.
      </Typography>

      {/* ── Orphaned force banner ──
          Devices left forced with no session behind them: a row-button force records
          none, so no expiry reaches them and no banner owned them. Without this the
          operator had no route back from the very screen that forced them. */}
      {orphanedForced > 0 && (
        <Box sx={{
          mt: "20px", display: "flex", alignItems: "flex-start", gap: "14px",
          bgcolor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
          borderRadius: "10px", p: "16px 20px",
        }}>
          <NotificationsActiveIcon sx={{ color: "#F59E0B", fontSize: "1.3rem", mt: "2px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#F59E0B", mb: "3px" }}>
              {orphanedForced} Tjommie{orphanedForced === 1 ? " is" : "s are"} still forced
            </Typography>
            <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>
              No switching session is running, so{" "}
              <strong style={{ color: "#e0e6f0" }}>nothing will release {orphanedForced === 1 ? "it" : "them"} automatically</strong>
              {" "}— row-button forces carry no timer or expiry. Clear {orphanedForced === 1 ? "it" : "them"} to return{" "}
              {orphanedForced === 1 ? "the Tjommie" : "those Tjommies"} to normal control.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px", flexShrink: 0, alignItems: "center" }}>
            <ButtonBase
              onClick={() => { setDeactivateOpen(true); }}
              sx={{
                bgcolor: "#F59E0B", color: "#431407", px: "14px", py: "7px",
                borderRadius: "7px", fontSize: "0.82rem", fontWeight: 700,
                "&:hover": { bgcolor: "#D97706" },
              }}
            >
              Clear forced
            </ButtonBase>
          </Box>
        </Box>
      )}

      {/* ── Alarm banner ── */}
      {bannerState === "alarm" && (
        <Box sx={{
          mt: "20px", display: "flex", alignItems: "flex-start", gap: "14px",
          bgcolor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
          borderRadius: "10px", p: "16px 20px",
        }}>
          <NotificationsActiveIcon sx={{ color: "#F59E0B", fontSize: "1.3rem", mt: "2px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#F59E0B", mb: "3px" }}>
              Alarm — switch still active
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
              <strong style={{ color: "#e0e6f0" }}>{actionLabel}</strong> has been active on{" "}
              <strong style={{ color: "#e0e6f0" }}>{modeLabel}</strong> for longer than the alarm window
              and hasn't been reverted. <strong style={{ color: "#e0e6f0" }}>Nothing has happened automatically</strong> — this
              is a warning only.{" "}
              {expiry > 0 && (
                <>The <strong style={{ color: "#e0e6f0" }}>{expiryLabel}</strong> still applies.</>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px", flexShrink: 0, alignItems: "center" }}>
            <ButtonBase
              onClick={() => { setDeactivateOpen(true); }}
              sx={{
                bgcolor: "#F59E0B", color: "#431407", px: "14px", py: "7px",
                borderRadius: "7px", fontSize: "0.82rem", fontWeight: 700,
                "&:hover": { bgcolor: "#D97706" },
              }}
            >
              Deactivate now
            </ButtonBase>
            <ButtonBase
              onClick={() => { setAlarmDismissed(true); setBannerState(null); }}
              sx={{
                bgcolor: "transparent", border: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.65)", px: "14px", py: "7px",
                borderRadius: "7px", fontSize: "0.82rem",
                "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
              }}
            >
              Dismiss
            </ButtonBase>
          </Box>
        </Box>
      )}

      {/* ── Expired banner ── */}
      {bannerState === "expired" && (
        <Box sx={{
          mt: "20px", display: "flex", alignItems: "flex-start", gap: "14px",
          bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: "10px", p: "16px 20px",
        }}>
          <WarningAmberIcon sx={{ color: "#FCA5A5", fontSize: "1.3rem", mt: "2px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#FCA5A5", mb: "3px" }}>
              Expired — Tjommies reverted automatically
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
              The <strong style={{ color: "#e0e6f0" }}>{expiryLabel}</strong> was reached without a manual
              deactivate, so the system executed the revert: affected Tjommies are back on{" "}
              <strong style={{ color: "#e0e6f0" }}>Nil</strong> (null) control. The override is{" "}
              <strong style={{ color: "#e0e6f0" }}>no longer active</strong>.
            </Typography>
          </Box>
          <ButtonBase
            onClick={() => setBannerState(null)}
            sx={{
              bgcolor: "transparent", border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.65)", px: "14px", py: "7px",
              borderRadius: "7px", fontSize: "0.82rem", flexShrink: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
            }}
          >
            Dismiss
          </ButtonBase>
        </Box>
      )}

      {/* ── Overridden-by-MaxReturn banner ── */}
      {cancelled && (
        <Box sx={{
          mt: "20px", display: "flex", alignItems: "flex-start", gap: "14px",
          bgcolor: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.25)",
          borderRadius: "10px", p: "16px 20px",
        }}>
          <WarningAmberIcon sx={{ color: "#7DD3FC", fontSize: "1.3rem", mt: "2px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#7DD3FC", mb: "3px" }}>
              {cancelled.closed
                ? "Overridden — the system ended your switch"
                : "Partially overridden — the system released some Tjommies"}
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
              Demand rose far enough that maximum return took{" "}
              <strong style={{ color: "#e0e6f0" }}>{cancelled.released}</strong>
              {cancelled.total ? <> of <strong style={{ color: "#e0e6f0" }}>{cancelled.total}</strong></> : null}{" "}
              Tjommie{cancelled.released === 1 ? "" : "s"} back off your override to keep the site under
              its demand threshold. This was <strong style={{ color: "#e0e6f0" }}>not an expiry</strong> and
              nothing timed out — the system acted on demand-safety grounds.{" "}
              {cancelled.closed
                ? <>The override is <strong style={{ color: "#e0e6f0" }}>no longer active</strong>; re-apply it if the demand event has passed.</>
                : <>The rest of the override is <strong style={{ color: "#e0e6f0" }}>still active</strong>.</>}
            </Typography>
          </Box>
          <ButtonBase
            onClick={() => { dismissedSessionRef.current = cancelled.id; setCancelled(null); }}
            sx={{
              bgcolor: "transparent", border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.65)", px: "14px", py: "7px",
              borderRadius: "7px", fontSize: "0.82rem", flexShrink: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
            }}
          >
            Dismiss
          </ButtonBase>
        </Box>
      )}

      {/* ── Session conflict ── */}
      {conflict && (
        <Box sx={{
          mt: "20px", display: "flex", alignItems: "flex-start", gap: "14px",
          bgcolor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
          borderRadius: "10px", p: "16px 20px",
        }}>
          <WarningAmberIcon sx={{ color: "#F59E0B", fontSize: "1.3rem", mt: "2px", flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#F59E0B", mb: "3px" }}>
              Not applied — a switching session is already open
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
              {conflict} <strong style={{ color: "#e0e6f0" }}>Nothing was switched.</strong> Only one
              override can be active at a time, whether it covers the whole fleet or a selection.
            </Typography>
          </Box>
          <ButtonBase
            onClick={() => setConflict(null)}
            sx={{
              bgcolor: "transparent", border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.65)", px: "14px", py: "7px",
              borderRadius: "7px", fontSize: "0.82rem", flexShrink: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
            }}
          >
            Dismiss
          </ButtonBase>
        </Box>
      )}

      {/* ── Bulk override card ── */}
      <Box sx={{ mt: "28px", bgcolor: "#161B22", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", overflow: "hidden" }}>

        {/* Card header */}
        <Box sx={{ px: "22px", py: "16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff" }}>Bulk override</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>
            Applies to <span style={{ color: "rgba(255,255,255,0.65)" }}>{isSelectMode ? "selected" : "all connected"}</span> Tjommies.
          </Typography>
        </Box>

        <Box sx={{ p: "22px" }}>

          {/* ── Row 1: MODE | SWITCH STATUS ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px", mb: "18px" }}>

            {/* A. MODE */}
            <Box>
              <FieldLabel letter="A">Mode</FieldLabel>
              <ModeToggle value={mode} onChange={setMode} />
              <Helper>
                Choose <strong style={{ color: "rgba(255,255,255,0.65)" }}>All</strong> for fleet-wide, or{" "}
                <strong style={{ color: "rgba(255,255,255,0.65)" }}>Select</strong> to pick individual serial numbers below.
              </Helper>
            </Box>

            {/* B. SWITCH STATUS */}
            <Box>
              <FieldLabel letter="B">Switch Status</FieldLabel>
              <StatusToggle value={switchStatus} onChange={setSwitchStatus} />
              <Helper>
                Force On — element energised.&nbsp; Force Off — element disabled.
              </Helper>
            </Box>
          </Box>

          {/* ── Row 2: STARTING DATE / TIME | ALARM | EXPIRY ── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "18px", mb: "18px" }}>

            {/* C. STARTING DATE / TIME */}
            <Box>
              <FieldLabel letter="C">Starting Date / Time</FieldLabel>
              <TextField
                type="datetime-local"
                size="small"
                fullWidth
                value={startDateTime}
                onChange={(e) => setStartDateTime(e.target.value)}
                sx={{
                  ...INPUT_SX,
                  colorScheme: "dark",
                  "& .MuiInputBase-input": { padding: "10px 12px", colorScheme: "dark" },
                }}
              />
              <Helper>Defaults to now. Set a future time to schedule the switch.</Helper>
            </Box>

            {/* D. ALARM ACTIVATION */}
            <Box>
              <FieldLabel
                letter="D"
                info={{
                  title: "Alarm — warning only",
                  body: "Fires this many minutes after the switch activates, surfacing as a warning banner at the top of this page. It only warns the operator that a switch is still active and hasn't been reverted — nothing happens automatically at the alarm threshold. Distinct from Expiry (E), which enforces. 0 = no alarm.",
                }}
              >
                Alarm Activation
              </FieldLabel>
              <UnitInput value={alarm} onChange={setAlarm} />
              <Helper>
                A warning banner appears this many minutes after the switch activates, so the operator
                can intervene. <strong style={{ color: "rgba(255,255,255,0.5)" }}>0 = no alarm.</strong>
              </Helper>
            </Box>

            {/* E. EXPIRY */}
            <Box>
              <FieldLabel
                letter="E"
                info={{
                  title: "Expiry — hard enforcement",
                  body: "Hard enforcement, not a reminder. If the operator hasn't manually deactivated by this time, the system automatically executes the revert — affected Tjommies return to Nil (null) control on their own. Counted from activation, in parallel with the alarm (D). 0 = no expiry (manual deactivate only).",
                }}
              >
                Expiry
              </FieldLabel>
              <UnitInput value={expiry} onChange={setExpiry} />
              <Helper>
                If not deactivated first, Tjommies <strong style={{ color: "rgba(255,255,255,0.5)" }}>automatically</strong> revert
                to Nil this long after the switch activates.{" "}
                <strong style={{ color: "rgba(255,255,255,0.5)" }}>0 = no expiry (manual deactivate only).</strong>
              </Helper>
            </Box>
          </Box>

          {/* ── Action bar: summary + switch button ── */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>

            {/* F. Summary */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "9px", flex: 1 }}>
              <Badge letter="F" />
              <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.5, mt: "-1px" }}>
                Ready to{" "}
                <strong style={{ color: "#fff" }}>{actionLabel}</strong>{" "}
                <strong style={{ color: "#fff" }}>{modeLabel}</strong> at{" "}
                <strong style={{ color: "#fff" }}>{formattedTime}</strong>, with a{" "}
                <strong style={{ color: "#fff" }}>{alarmLabel}</strong>{" "}
                and <strong style={{ color: "#fff" }}>{expiryLabel}</strong>.
              </Typography>
            </Box>

            {/* H. Switch / Deactivate / Use per-device buttons */}
            {isSelectMode ? (
              <ButtonBase
                disabled
                sx={{
                  display: "flex", alignItems: "center", gap: "10px",
                  borderRadius: "10px", px: "28px", py: "16px", flexShrink: 0, minWidth: 220, justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.35)",
                  fontSize: "0.95rem", fontWeight: 700, letterSpacing: "0.02em",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: "1.2rem" }} />
                Use per-device buttons
              </ButtonBase>
            ) : active ? (
              <ButtonBase
                onClick={handleSwitchClick}
                sx={{
                  display: "flex", alignItems: "center", gap: "10px",
                  borderRadius: "10px", px: "28px", py: "16px", flexShrink: 0, minWidth: 220, justifyContent: "center",
                  bgcolor: "#EF4444", color: "#FEE2E2",
                  fontSize: "0.95rem", fontWeight: 700, letterSpacing: "0.02em",
                  boxShadow: "0 6px 20px rgba(239,68,68,0.28)",
                  transition: "all 0.22s cubic-bezier(0.4,0,0.2,1)",
                  "&:hover": { bgcolor: "#DC2626", boxShadow: "0 8px 26px rgba(239,68,68,0.36)", transform: "translateY(-1px)" },
                  "&:active": { transform: "translateY(0)" },
                }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: "1.2rem" }} />
                Deactivate
              </ButtonBase>
            ) : (
              <ButtonBase
                onClick={handleSwitchClick}
                sx={{
                  display: "flex", alignItems: "center", gap: "10px",
                  borderRadius: "10px", px: "28px", py: "16px", flexShrink: 0, minWidth: 220, justifyContent: "center",
                  bgcolor: switchColor, color: isForceOn ? "#052E16" : "#450A0A",
                  fontSize: "0.95rem", fontWeight: 700, letterSpacing: "0.02em",
                  boxShadow: switchShadow,
                  transition: "all 0.22s cubic-bezier(0.4,0,0.2,1)",
                  "&:hover": { bgcolor: switchHoverBg, boxShadow: switchHoverShadow, transform: "translateY(-1px)" },
                  "&:active": { transform: "translateY(0)" },
                }}
              >
                <PowerSettingsNewIcon sx={{ fontSize: "1.2rem" }} />
                Switch
              </ButtonBase>
            )}
          </Box>

          {/* ── Status row (C + G) ── */}
          <Box sx={{
            display: "grid", gridTemplateColumns: "1fr auto", alignItems: "stretch", gap: "18px",
            p: "16px 18px", bgcolor: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: "10px", mt: "20px",
          }}>
            {/* Status chip */}
            <Box>
              {active ? (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "8px",
                  px: "14px", py: "8px", borderRadius: "999px",
                  bgcolor: isForceOn ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                  border: `1px solid ${isForceOn ? "rgba(34,197,94,0.22)" : "rgba(239,68,68,0.32)"}`,
                  color: isForceOn ? "#86EFAC" : "#FCA5A5",
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "currentColor", animation: "pulse 1.5s ease-in-out infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.4 } } }} />
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "inherit" }}>
                    Active — {isForceOn ? "Force On" : "Force Off"}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", px: "14px", py: "8px", borderRadius: "999px", bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.65)" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.35)" }} />
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "rgba(255,255,255,0.65)" }}>
                    {isSelectMode ? "Idle — using per-device controls" : "Idle — no switch active"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* G. Time since switch */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-end" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
                <Badge letter="G" />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.08em", color: "rgba(255,255,255,0.45)", textTransform: "uppercase" }}>
                  Time Since Switch
                </Typography>
              </Box>
              <Typography sx={{
                fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 600,
                color: active ? "#fff" : "rgba(255,255,255,0.35)",
                letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums",
              }}>
                {active && elapsed != null ? formatElapsed(elapsed) : "—"}
                <Typography component="span" sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", fontWeight: 500, ml: "4px", letterSpacing: 0, fontFamily: "inherit" }}>m</Typography>
              </Typography>
            </Box>
          </Box>

        </Box>
      </Box>

      <PerDeviceTable
        mode={mode}
        onRequestSelectionForce={onRequestSelectionForce}
        selectionBusy={selectionBusy}
      />

      {/* Confirm modal */}
      {/* Selection force - same modal, same alarm and expiry, different scope line */}
      <ConfirmModal
        open={pendingSelection !== null}
        onClose={() => setPendingSelection(null)}
        onConfirm={handleSelectionForceConfirm}
        direction={pendingSelection?.forcedStatus === "ForcedOn" ? "force-on" : "force-off"}
        scope={pendingSelection
          ? `${pendingSelection.serials.length} selected Tjommie${pendingSelection.serials.length === 1 ? "" : "s"}`
          : ""}
        start="Immediately"
        alarm={alarm > 0 ? `${alarm} min after activation (warning only)` : "None"}
        expiry={expiry > 0
          ? `${expiry} min after activation (auto-reverts to Nil)`
          : "Manual deactivate only"
        }
      />

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        direction={switchStatus}
        scope="All connected Tjommies (fleet-wide)"
        start={(() => {
          const dt2 = dayjs(startDateTime);
          const isFuture = dt2.isValid() && dt2.isAfter(dayjs().add(1, "minute"));
          return isFuture ? dt2.format("HH:mm [on] DD/MM/YYYY") : "Immediately";
        })()}
        alarm={alarm > 0 ? `${alarm} min after activation (warning only)` : "None"}
        expiry={expiry > 0
          ? `${expiry} min after activation (auto-reverts to Nil)`
          : "Manual deactivate only"
        }
      />

      {/* Deactivate confirm modal */}
      {deactivateOpen && (
        <Box
          onClick={(e) => { if (e.target === e.currentTarget) setDeactivateOpen(false); }}
          sx={{
            position: "fixed", inset: 0, zIndex: 1300,
            bgcolor: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            p: "24px",
          }}
        >
          <Box sx={{
            bgcolor: "#0D1117", border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "14px", p: "24px", width: "100%", maxWidth: 480,
          }}>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#fff", mb: "8px" }}>
              Deactivate switch?
            </Typography>
            <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
              Resets all affected Tjommies to Nil (null) status now, cancels the active
              override and clears its alarm and expiry timers — regardless of the countdown.
            </Typography>
            <Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-end", mt: "22px" }}>
              <ButtonBase
                onClick={() => setDeactivateOpen(false)}
                sx={{
                  bgcolor: "transparent", border: "none",
                  color: "rgba(255,255,255,0.65)", px: "14px", py: "8px",
                  borderRadius: "8px", fontSize: "0.85rem", minWidth: 116, justifyContent: "center",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.04)", color: "#fff" },
                }}
              >
                Cancel
              </ButtonBase>
              <ButtonBase
                onClick={handleDeactivateConfirm}
                sx={{
                  border: "none", px: "20px", py: "8px", borderRadius: "8px",
                  fontSize: "0.85rem", fontWeight: 700, minWidth: 116, justifyContent: "center",
                  bgcolor: "#EF4444", color: "#FEE2E2",
                  "&:hover": { bgcolor: "#DC2626" },
                }}
              >
                Deactivate
              </ButtonBase>
            </Box>
          </Box>
        </Box>
      )}

      <Snackbar
        open={!!deactivateToast}
        autoHideDuration={6000}
        onClose={() => setDeactivateToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={deactivateToast?.level || "info"} onClose={() => setDeactivateToast(null)} variant="filled">
          {deactivateToast?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
